import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const ZOOM_STEPS = [1, 1.25, 1.5, 2];

/**
 * تمام‌صفحه بدون MUI Dialog — برای جلوگیری از modal تو در تو.
 */
const FullscreenImageOverlay = ({ open, onClose, label, src }) => {
  const [zoomIndex, setZoomIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setZoomIndex(0);
    setLoading(Boolean(src));
    setFailed(false);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, src, onClose]);

  if (!open || typeof document === "undefined") return null;

  const zoom = ZOOM_STEPS[zoomIndex];

  return createPortal(
    <Box
      role="dialog"
      aria-modal="true"
      aria-label={label || "پیش‌نمایش تصویر"}
      onClick={onClose}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1600,
        bgcolor: "rgba(0, 0, 0, 0.92)",
        display: "flex",
        flexDirection: "column",
        p: { xs: 1, sm: 2 },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ flexShrink: 0, mb: 1 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography variant="subtitle2" color="common.white" noWrap>
          {label || "پیش‌نمایش"}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            aria-label="بزرگ‌نمایی"
            disabled={zoomIndex >= ZOOM_STEPS.length - 1 || failed}
            onClick={() =>
              setZoomIndex((i) => Math.min(i + 1, ZOOM_STEPS.length - 1))
            }
            sx={{ color: "common.white" }}
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="کوچک‌نمایی"
            disabled={zoomIndex <= 0 || failed}
            onClick={() => setZoomIndex((i) => Math.max(i - 1, 0))}
            sx={{ color: "common.white" }}
          >
            <ZoomOutIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            aria-label="بستن"
            onClick={onClose}
            sx={{ color: "common.white" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto",
        }}
      >
        {loading && !failed ? (
          <CircularProgress sx={{ color: "common.white" }} size={32} />
        ) : null}
        {failed ? (
          <Typography color="common.white">بارگذاری تصویر ممکن نشد.</Typography>
        ) : null}
        {src && !failed ? (
          <Box
            component="img"
            src={src}
            alt={label}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setFailed(true);
            }}
            sx={{
              maxWidth: "100%",
              maxHeight: "calc(100vh - 72px)",
              objectFit: "contain",
              transform: `scale(${zoom})`,
              transition: "transform 0.2s ease",
              display: loading ? "none" : "block",
            }}
          />
        ) : null}
      </Box>
    </Box>,
    document.body
  );
};

FullscreenImageOverlay.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  label: PropTypes.string,
  src: PropTypes.string,
};

export default FullscreenImageOverlay;
